/**
 * TanStack Query cache keys, spelled once (spec §1.1).
 *
 * WHY A FACTORY RATHER THAN INLINE ARRAYS. A mutation has to invalidate the
 * lists a screen it never imported is showing. `queryClient.invalidateQueries({
 * queryKey: queryKeys.customers.all })` only works if every customer query in
 * the app agreed on the same prefix — and "agreed" cannot be enforced when the
 * array is typed out at each call site. Four screen authors, one prefix per
 * entity, no drift.
 *
 * The shape is always `[entity, "list" | "detail", …]` so a prefix match on
 * `[entity]` catches everything and a prefix match on `[entity, "list"]`
 * catches only the paged screens.
 *
 * Filter objects are part of the key BY VALUE. TanStack Query hashes keys
 * structurally, so `{ status: undefined }` and `{}` are the same key — which is
 * what makes "no filter selected" reuse the cache entry the screen already
 * painted rather than refetching.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * EVERY ENTITY IN THE APP IS HERE, AND THAT WAS NOT TRUE UNTIL 2026-08-16.
 *
 * This file's stated purpose is "spelled once", and two features had opted out:
 * `challanKeys` was declared in `features/challans/use-challan-list.ts` and
 * `paymentKeys` in `features/payments/use-payments.ts`, each with a comment
 * saying it belonged here and was only sitting locally because this file was
 * owned by another change in flight. Both are folded in below, at the same key
 * VALUES, so no cache entry was orphaned by the move; the feature modules
 * re-export the names so no call site changed.
 *
 * The cost of the split was not theoretical. A mutation author reaching for
 * `queryKeys` to invalidate after a write found no challan prefix and reached
 * for a bare `queryClient.invalidateQueries()` instead — nuking the whole cache
 * on a workshop mobile connection. Worse, the NEXT author will find one and
 * invalidate only what it lists. A stale badge after a write is how a screen
 * starts lying.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/** A filter bag as it goes into a key. Values are already API-shaped. */
export type QueryFilters = Record<string, string | number | boolean | undefined>;

export const queryKeys = {
  customers: {
    all: ["customers"] as const,
    lists: () => ["customers", "list"] as const,
    list: (filters: QueryFilters) => ["customers", "list", filters] as const,
    detail: (id: number | string) => ["customers", "detail", String(id)] as const,
    /** The GSTIN check is a read of a pure function of its input — cache it. */
    gstin: (gstin: string) => ["customers", "gstin", gstin] as const,
  },

  items: {
    all: ["items"] as const,
    lists: () => ["items", "list"] as const,
    list: (filters: QueryFilters) => ["items", "list", filters] as const,
    detail: (id: number | string) => ["items", "detail", String(id)] as const,
  },

  invoices: {
    all: ["invoices"] as const,
    lists: () => ["invoices", "list"] as const,
    list: (filters: QueryFilters) => ["invoices", "list", filters] as const,
    detail: (id: number | string) => ["invoices", "detail", String(id)] as const,
  },

  quotations: {
    all: ["quotations"] as const,
    lists: () => ["quotations", "list"] as const,
    list: (filters: QueryFilters) => ["quotations", "list", filters] as const,
    detail: (id: number | string) => ["quotations", "detail", String(id)] as const,
  },

  /**
   * §17.7 — delivery challans. The third register, and the third block here,
   * shaped exactly like the two above it.
   */
  challans: {
    all: ["challans"] as const,
    lists: () => ["challans", "list"] as const,
    list: (filters: QueryFilters) => ["challans", "list", filters] as const,
    detail: (id: number | string) => ["challans", "detail", String(id)] as const,
  },

  /**
   * §17.3 — credit notes. The fourth register, shaped exactly like the three
   * above it.
   *
   * `list({ invoiceId })` IS ALSO WHAT THE BILL'S OWN SCREEN READS, not a
   * separate `forInvoice(id)` key. It is the same `GET /credit-notes?invoiceId=`
   * with the same filter bag, so issuing or cancelling a note invalidates the
   * register and the card under the invoice with one prefix match on
   * `["creditNotes", "list"]` — which is the whole reason filters are part of
   * the key by value.
   *
   * INVALIDATE `payments` AND `dashboard` ALONGSIDE ANY WRITE HERE. An issued
   * credit note changes what the customer owes (§17.5's outstanding is
   * `grandTotal − received − credited`), so the bill's payment card, the unpaid
   * register, the statement and the home-screen figure all move — and none of
   * them is under this prefix.
   */
  creditNotes: {
    all: ["creditNotes"] as const,
    lists: () => ["creditNotes", "list"] as const,
    list: (filters: QueryFilters) => ["creditNotes", "list", filters] as const,
    detail: (id: number | string) => ["creditNotes", "detail", String(id)] as const,
  },

  /**
   * §17.5 — the payments ledger.
   *
   * NOT SHAPED LIKE THE REGISTERS, and deliberately: payments are not a register
   * the owner pages through, they hang off an invoice. THERE IS NO
   * `list(filters)` — `GET /payments/outstanding` takes no parameters at all, so
   * there is no filter bag to key by, and adding one "for symmetry" would
   * advertise a filtered payment list that does not exist.
   *
   * PAYMENTS ATTACH TO INVOICES ONLY (§17.5) — never to a quotation, which is an
   * offer, and never to a challan, which creates no receivable. That is why
   * `invoice(id)` is spelled out rather than parameterised by document kind.
   */
  payments: {
    all: ["payments"] as const,
    /** The ledger and position for ONE bill. */
    invoice: (invoiceId: number | string) =>
      ["payments", "invoice", String(invoiceId)] as const,
    /** "Who owes me money" — the whole position, unpaged. */
    outstanding: () => ["payments", "outstanding"] as const,
    /** One customer's statement. */
    statement: (customerId: number | string) =>
      ["payments", "statement", String(customerId)] as const,
  },

  /**
   * The home screen (§11.2). One key, no filters — `GET /dashboard/summary`
   * takes only `?fy=` and Home never sends it.
   *
   * INVALIDATE THIS AFTER EVERY DOCUMENT WRITE. Saving a bill changes "This
   * month", the last bill and the recent list; without an invalidation the
   * owner taps Home straight after saving and sees the figures from before he
   * saved, which reads as the bill not having been saved at all.
   */
  dashboard: {
    all: ["dashboard"] as const,
    summary: () => ["dashboard", "summary"] as const,
  },

  /**
   * The notification hub.
   *
   * `unreadCount()` IS ITS OWN KEY, NOT A SLICE OF A LIST. The header polls it
   * on every screen and reads one integer from `/notifications/unread-count`;
   * keying it under `["notifications", "list", …]` would make opening the hub
   * and the badge share a cache entry whose two producers return different
   * shapes.
   *
   * INVALIDATE THE WHOLE `["notifications"]` PREFIX AFTER A MARK-READ. The badge
   * and the list move together — a hub that greys a row while the bell still
   * says 3 is the screen contradicting itself in the same viewport. And after
   * RECORDING AN E-WAY BILL NUMBER, invalidate this too: the server raises
   * `invoice.eway_bill.recorded` inside that same transaction, so somebody's
   * badge changed as a result of the write.
   */
  notifications: {
    all: ["notifications"] as const,
    lists: () => ["notifications", "list"] as const,
    list: (filters: QueryFilters) => ["notifications", "list", filters] as const,
    unreadCount: () => ["notifications", "unread-count"] as const,
  },

  /**
   * The shared conversation (§2's three people).
   *
   * ITS OWN PREFIX, NOT A SLICE OF `notifications`, and the reason is the same
   * one that keeps the two tables apart: sending a message must invalidate the
   * conversation and its badge, and must NOT invalidate somebody's compliance
   * inbox. One prefix for both would make every message send re-fetch the e-way
   * bill alerts, and — worse — would tempt a future change to merge the two
   * badges into one number that neither reading action can fully clear.
   *
   * `unreadCount()` IS ITS OWN KEY for the same reason the bell's is: the header
   * polls one integer on every screen, and keying it under the list would make
   * the badge and the conversation share a cache entry whose two producers
   * return different shapes.
   *
   * THE LIST KEY CARRIES NO CURSOR. Paging is "load older" through
   * `fetchNextPage`, so every page of one conversation belongs to ONE cache
   * entry — a key per cursor would leave the newly-sent message appended to a
   * page nobody is looking at.
   */
  messages: {
    all: ["messages"] as const,
    list: () => ["messages", "list"] as const,
    unreadCount: () => ["messages", "unread-count"] as const,
    /**
     * THE PRIVATE CONVERSATIONS SIT UNDER THE SAME `all` PREFIX, DELIBERATELY.
     *
     * Sending a private message must refresh the conversation list beside the
     * thread (its last line and its unread), and the room's send must not leave
     * a stale private badge in the header, so one `invalidateQueries({ queryKey:
     * all })` moves the whole messenger at once. The room and the private half
     * are one feature behind one flag; they are not one CACHE ENTRY, which is
     * what the separate leaves below are for.
     */
    direct: {
      /** `GET /messages/direct` — the conversation list AND the people directory. */
      list: () => ["messages", "direct", "list"] as const,
      /**
       * One conversation, keyed BY THE PEER'S USER ID.
       *
       * The id is in the key because read state is per conversation: a single
       * entry shared by every peer would show the son's thread under the CA's
       * name for one paint, and would clear the wrong badge if a watermark
       * were computed from it. NO CURSOR though — "load older" is
       * `fetchNextPage`, so every page of one conversation is one entry.
       */
      thread: (peerUserId: number) => ["messages", "direct", "thread", peerUserId] as const,
    },
  },

  /**
   * Reports (§11.2, secondary) — keyed by the period the owner picked.
   *
   * ───────────────────────────────────────────────────────────────────────────
   * THE PERIOD IS IN THE KEY BY VALUE, and it is the PRESET rather than the two
   * resolved dates. The screen sends `?preset=` and the SERVER resolves the
   * window (`resolveReportWindow`), so the client never computes April
   * boundaries a second time — and a cache keyed on dates the client derived
   * would be keyed on a window the server might not agree with.
   *
   * `ageing` TAKES NO PERIOD, and that is the endpoint's decision rather than an
   * omission here: outstanding is a to-do list, not a period figure, and the
   * bill worth chasing hardest is the oldest one, which any period scope would
   * hide. There is nothing to key by.
   *
   * `monthly` IS A DASHBOARD PAYLOAD UNDER A REPORTS KEY, deliberately. The
   * billed-by-month chart is fed by `GET /dashboard/summary?fy=`, and Home reads
   * the SAME endpoint with no `?fy=` at all under `dashboard.summary()`. Keying
   * this one there too would let a Reports screen showing last financial year
   * overwrite the cache entry Home paints from, and the owner would tap Home to
   * find last year's figures presented as this month's.
   * ───────────────────────────────────────────────────────────────────────────
   */
  reports: {
    all: ["reports"] as const,
    invoices: (from: string, to: string) => ["reports", "invoices", from, to] as const,
    summary: (period: string) => ["reports", "summary", period] as const,
    customers: (period: string) => ["reports", "customers", period] as const,
    ageing: () => ["reports", "ageing"] as const,
    monthly: (fy: string) => ["reports", "monthly", fy] as const,
  },

  /**
   * §2, §13 — the accounts behind `user.manage`.
   *
   * SHAPED LIKE A REGISTER even though the table holds three rows, because the
   * screen genuinely pages and filters it (`?role=&active=`) and because every
   * write here has to invalidate every list: creating an account, changing a
   * role and resetting a credential all move `hasCredential`, `lockedUntil` or
   * `roleAvailable` on a row some other filter is showing.
   *
   * THERE IS NO `detail(id)` KEY, AND THAT IS NOT AN OMISSION. `GET /users/:id`
   * exists on the API, but nothing in the SPA opens one account on its own — the
   * screen edits rows in place, from the list it already holds. A key with no
   * producer is a key the next author writes a query against without noticing
   * there is no screen behind it.
   */
  users: {
    all: ["users"] as const,
    lists: () => ["users", "list"] as const,
    list: (filters: QueryFilters) => ["users", "list", filters] as const,
  },

  /**
   * §2, §2.1 — MY OWN account, behind `profile.manage`, which every role holds.
   *
   * ITS OWN PREFIX RATHER THAN A SLICE OF `users`, for the reason the API gives
   * `/profile` its own router: the two answer different questions, and one is
   * reachable by three roles while the other is ADMIN-only. Folding "me" into
   * `users.detail(id)` would make every profile write invalidate the ADMIN's
   * account table, and — the sharper half — would tempt somebody to render the
   * account list out of this entry, which is a list this role cannot fetch.
   *
   * ONE KEY, NO PARAMETERS. `GET /profile` has no `:id` segment and takes no
   * query, because there is exactly one row it can answer with. A `detail(id)`
   * here would advertise a request that cannot be made.
   *
   * NOTHING A SECRET WAS TYPED INTO GOES NEAR IT. Query keys are hashed, kept
   * for the life of the cache and printed by the devtools; a PIN in one would
   * outlive the request that carried it by hours. The credential and identifier
   * writes are MUTATIONS, keyed by nothing, and their variables die with them.
   */
  profile: {
    all: ["profile"] as const,
    me: () => ["profile", "me"] as const,
  },

  /**
   * §13, §14.3 — the audit window behind `audit.view`.
   *
   * THE OFFSET IS PART OF THE KEY, unlike every register above, and it has to
   * be: `GET /audit` answers no `total`, so the screen cannot render a page
   * count and pages on `hasMore` instead. Each page is therefore its own cache
   * entry, and stepping back to the previous one is a cache hit rather than a
   * second scan of a six-year table.
   *
   * NOTHING INVALIDATES THIS, EVER. `audit_log` is append-only and reading it
   * writes no row (services/audit-query.ts), so there is no mutation in this app
   * whose success should evict a page of it. A write elsewhere does add a row —
   * the reader refetches by asking again, which is what the refresh control is
   * for.
   */
  audit: {
    all: ["audit"] as const,
    lists: () => ["audit", "list"] as const,
    list: (filters: QueryFilters) => ["audit", "list", filters] as const,
  },

  /**
   * §18.1 — the feature flags behind `settings.write`.
   *
   * ONE KEY, NO FILTERS AND NO DETAIL. `GET /flags` takes no query parameters
   * and answers every flag in one shot — there are thirteen of them and the
   * §18.4 counts on the response (`liveNonPermanent`, `stale`) are folds over
   * the WHOLE list, so a filtered or paged variant would produce a count that
   * describes something narrower than the sentence beside it claims.
   *
   * A TOGGLE INVALIDATES `all`, WHICH IS THE SAME ENTRY — spelled as a prefix
   * anyway, so the invalidation keeps working if a second flag query is ever
   * added. It must NOT invalidate anything else: the flag the toggle moved may
   * well be the one gating a module whose queries are all 404ing, and evicting
   * those would refetch a wall of 404s on a workshop connection to learn nothing
   * the reader asked for. The reader lands on the module's own screen next, and
   * that screen asks for itself.
   */
  flags: {
    all: ["flags"] as const,
    list: () => ["flags", "list"] as const,
  },

  hsn: {
    all: ["hsn"] as const,
    search: (q: string) => ["hsn", "search", q] as const,
    frequent: () => ["hsn", "frequent"] as const,
  },
} as const;
